import React, { useState, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { areaElementClasses, lineElementClasses } from '@mui/x-charts/LineChart';
import { chartsAxisHighlightClasses } from '@mui/x-charts/ChartsAxisHighlight';

const VisitasSparkline = ({ visitas = [] }) => {
  const [dayIndex, setDayIndex] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [days, setDays] = useState([]);

  useEffect(() => {
    // Generar datos de los últimos 30 días
    const today = new Date();
    const last30Days = [];
    const visitasCount = [];
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      
      const dateStr = date.toISOString().split('T')[0];
      const dayLabel = date.toLocaleDateString('es-ES', { 
        day: 'numeric', 
        month: 'short' 
      });
      
      // Contar visitas creadas en esta fecha
      const visitasEnFecha = visitas.filter(visita => {
        if (!visita.FechaCreacionRegistro) return false;
        const visitaDate = new Date(visita.FechaCreacionRegistro).toISOString().split('T')[0];
        return visitaDate === dateStr;
      }).length;
      
      last30Days.push(dayLabel);
      visitasCount.push(visitasEnFecha);
    }
    
    setDays(last30Days);
    setChartData(visitasCount);
  }, [visitas]);

  const settings = {
    data: chartData,
    baseline: 'min',
    margin: { bottom: 0, top: 5, left: 4, right: 0 },
    xAxis: { 
      id: 'day-axis', 
      data: days,
    },
    yAxis: {
      domainLimit: (_, maxValue) => ({
        min: -maxValue / 6,
        max: maxValue,
      }),
    },
    sx: {
      [`& .${areaElementClasses.root}`]: { opacity: 0.2 },
      [`& .${lineElementClasses.root}`]: { strokeWidth: 3 },
      [`& .${chartsAxisHighlightClasses.root}`]: {
        stroke: 'rgb(59, 130, 246)', // blue-500
        strokeDasharray: 'none',
        strokeWidth: 2,
      },
    },
    slotProps: {
      lineHighlight: { r: 4 },
    },
    clipAreaOffset: { top: 2, bottom: 2 },
    axisHighlight: { x: 'line' },
    showTooltip: false,
    // disableAxisListener: false,
  };

  const totalVisitas = chartData.reduce((sum, count) => sum + count, 0);

  return (
    <Box
      onKeyDown={(event) => {
        switch (event.key) {
          case 'ArrowLeft':
            setDayIndex((p) =>
              p === null ? days.length - 1 : (days.length + p - 1) % days.length,
            );
            break;
          case 'ArrowRight':
            setDayIndex((p) => (p === null ? 0 : (p + 1) % days.length));
            break;
          default:
        }
      }}
      onFocus={() => {
        setDayIndex((p) => (p === null ? 0 : p));
      }}
      role="button"
      aria-label="Mostrando visitas creadas en el último mes"
      tabIndex={0}
      width="100%"
      height="100%"
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <Stack direction="column" width="100%">
        <Typography
          sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'rgb(107, 114, 128)', // gray-500
                fontWeight: 400,
                fontSize: '14px',
                pt: 1,
                fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          }}
        >
          {dayIndex === null
            ? 'Visitas creadas (último mes)'
            : `Fecha: ${days[dayIndex]}`}
        </Typography>
        <Stack
          direction="row"
          paddingTop={2}
          justifyContent="space-between"
          alignItems="flex-end"
          sx={{ borderBottom: 'solid 2px rgba(59, 130, 246, 0.2)' }} // blue-500 with opacity
        >
          <Typography sx={{ 
            fontSize: { xs: '1.5rem', sm: '2rem' }, // xs: mobile, sm: tablet+
            fontWeight: 600, 
            color: '#1f2937',
            fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
          }}> {/* gray-800 */}
            {dayIndex === null 
              ? totalVisitas.toLocaleString('es-ES')
              : (chartData[dayIndex] || 0).toLocaleString('es-ES')
            }
          </Typography>

          {chartData.length > 0 && (
            <Box
              onMouseMove={(event) => {
                const box = event.currentTarget.getBoundingClientRect();
                const chartWidth = box.width;
                const x = event.clientX - box.left;
                const chartWidthCalc = chartWidth - settings.margin.left - settings.margin.right;
                const index = Math.floor((x - settings.margin.left) / chartWidthCalc * days.length);
                
                if (index >= 0 && index < days.length) {
                  setDayIndex(index);
                }
              }}
              onMouseLeave={() => {
                setDayIndex(null);
              }}
              sx={{ 
                cursor: 'crosshair', 
                position: 'relative',
                width: '80%',
                height: 70,
              }}
            >
              {/* Líneas verticales de fondo */}
              <svg 
                width="100%" 
                height={70}
                preserveAspectRatio="none"
                style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
              >
                {days.map((_, index) => {
                  const x = `${(index / (days.length - 1)) * 100}%`;
                  return (
                    <line
                      key={index}
                      x1={x}
                      y1={settings.margin.top}
                      x2={x}
                      y2={70}
                      stroke="rgba(59, 130, 246, 0.15)"
                      strokeWidth={1.5}
                      vectorEffect="non-scaling-stroke"
                    />
                  );
                })}
              </svg>
              
              <SparkLineChart
                height={70}
                area
                showHighlight
                showTooltip={false}
                color="rgb(59, 130, 246)" // blue-500
                highlightedItem={
                  dayIndex === null
                    ? undefined
                    : { seriesId: 'auto-generated-id-0', dataIndex: dayIndex }
                }
                {...settings}
              />
            </Box>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

// function VisitasIcon(props) {
//   return (
//     <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
//       <title>Visitas</title>
//       <g>
//         <path
//           d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
//           fill="none"
//           stroke="currentColor"
//           strokeWidth={2}
//           strokeLinecap="round"
//           strokeLinejoin="round"
//         />
//       </g>
//     </svg>
//   );
// }

export default VisitasSparkline;
